import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Products', href: '/products' },
  { label: 'About Us', href: '/about' },
  { label: 'Certificates', href: '/certificates' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Bulk Supply', href: '/bulk-supply' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { openEnquiry } = useEnquiryModal();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    // Run once on mount to set correct initial state
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On non-home pages, always treat as "scrolled" so text is visible against white background
  const showScrolled = !isHome || scrolled;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolled
          ? 'bg-background-50 shadow-[0_2px_10px_rgba(0,0,0,0.08)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-105 ${
              showScrolled ? 'bg-primary-500' : 'bg-background-50/20'
            }`}>
              <i className={`text-lg md:text-xl transition-colors duration-300 ${
                showScrolled ? 'ri-plant-fill text-background-50' : 'ri-plant-fill text-background-50'
              }`}></i>
            </div>
            <div className="flex flex-col">
              <span className={`font-heading font-bold text-base md:text-lg leading-tight whitespace-nowrap transition-colors duration-300 ${
                showScrolled ? 'text-foreground-900' : 'text-background-50'
              }`}>
                Bassi Enterprises
              </span>
              <span className={`text-[10px] md:text-xs whitespace-nowrap transition-colors duration-300 ${
                showScrolled ? 'text-foreground-600' : 'text-background-50/80'
              }`}>
                Fresh Fruits & Vegetables
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    showScrolled
                      ? isActive
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50'
                      : isActive
                        ? 'text-background-50 bg-background-50/15'
                        : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full transition-colors duration-300 ${
                      showScrolled ? 'bg-primary-500' : 'bg-background-50'
                    }`} />
                  )}
                </Link>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <button
              onClick={() => openEnquiry()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill text-lg"></i>
              Bulk Supply Quote
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 cursor-pointer ${
              showScrolled ? 'text-foreground-900 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-[500px]' : 'max-h-0'
        }`}
      >
        <div className="bg-background-50 border-t border-background-200 px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            onClick={() => openEnquiry()}
            className="mt-2 flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-fill text-lg"></i>
            Bulk Supply Quote
          </button>
        </div>
      </div>
    </nav>
  );
}